"use client";
import { useAuth } from "@/store/auth";
import {
    Building2,
    Camera,
    CheckCircle,
    Trash,
    UploadCloud,
} from "lucide-react";
import { useSearchParams } from "next/navigation";
import React, { useState, useEffect, SetStateAction } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import ImageEditor from "./ImageEditor";
import { toast } from "react-toastify";
import { z } from "zod";
import {
    useGetCompanyDetail,
    useGetUserProfileInfo,
    useUpdateUserProfilePhoto,
    useCompanyInfo,
    useUpdateCompanyInfo,
    useGetGSTNumberVerificationMutation,
    useGetUserKycList,
    useUnAssignedBookingMutation,
    useUpdateUserDoc,
    useGetUpdateUserDoc,
    useBankUserDetailList,
    useGetCountries,
    useGetStates,
    useGetCities,
    useGetCompanyDetailsId,
    useGetUserCompanyInfo,
} from "../hooks/useCommon";

const companySchema = z.object({
    company_name: z.string().min(1, "Company Name is required"),
    country: z.string().min(1, "Country is required"),
    state: z.string().min(1, "State is required"),
    company_address: z.string().min(1, "Company Address is required"),
    pincode: z.string().optional(),
    pancard_no: z.string().min(1, "PAN No. is required"),
    gst: z.string().min(1, "Service Tax/GST is required"),
    city: z.string().optional(),
    contact_person_name: z.string().optional(),
    landline_no: z.string().optional(),
    mobile_no: z.string().optional(),
    email: z.string().optional(),
    website_url: z.string().optional(),
    active: z.string().optional(),
    constitution: z.string().optional(),
    gst_number: z.string(),
    gst_centre: z.string().optional(),
    center_code: z.string().optional(),
    einvoice_enabled: z.string().optional(),
    gst_expiry_date: z.string().optional(),
    gst_state: z.string().optional(),
    filings: z.string().optional(),
    gst_type: z.string().optional(),
    last_updated_on: z.string().optional(),
    legal_name: z.string().optional(),
    nature_of_business: z.string().optional(),
    primary_address: z.string().optional(),
    registered_on: z.string().optional(),
    state_code: z.string().optional(),
    trade_name: z.string().optional(),
    company_size: z.string().optional(),
    pan: z.string().optional(),
    branch: z.string().optional(),
});

const companySizes = [
    { value: "1", label: "1-10" },
    { value: "2", label: "10-20" },
    { value: "3", label: "20-50" },
    { value: "4", label: "50-100" },
    { value: "5", label: "100-300" },
];

export type companyData = z.infer<typeof companySchema>;

type UserCompanyProps = {
    setCounter: React.Dispatch<React.SetStateAction<number>>;
    counter: number;
    id: number;
};

export default function UserCompanyDetailForm({
                                                  setCounter,
                                                  counter,
                                                  id,
                                              }: UserCompanyProps) {
    const { data: getCompanyDetail } = useGetCompanyDetail();
    const companyProfileMutation = useCompanyInfo();

    const [status, setStatus] = useState<string>("1");

    const handleStatusToggle = (buttonId: string) => {
        setValue("branch", buttonId);
        setStatus((prev) => (prev === buttonId ? "1" : buttonId));
    };

    // console.log(getCompanyDetail);
    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        setValue,
        resetField,
        watch,
        getValues,
        control,
    } = useForm<companyData>({
        resolver: zodResolver(companySchema),
    });
    const companyDetailMutation = useUpdateCompanyInfo();
    const searchParams = useSearchParams();
    const user_id = searchParams.get("user_id");

    const path = searchParams.get("type");
    const { user: authUser } = useAuth();
    const userId = authUser?.id;

    const { data: companyDetailsData, refetch: refetchCompanyDetails } =
        useGetUserCompanyInfo();

    const [profilePreview, setProfilePreview] = useState<string | null>(null);
    const [addressProof, setAddressProof] = useState<string | null>(null);
    const [GSTProof, setGSTProof] = useState<string | null>(null);
    const [panProof, setPanProof] = useState<string | null>(null);
    const [logo, setLogo] = useState<string | null>(null);

    const { data: docData, refetch } = useGetUpdateUserDoc();
    const getCompanyDetailData =
        Array.isArray(getCompanyDetail) && getCompanyDetail.length > 0
            ? getCompanyDetail[0]
            : null;
    const { mutate: uploadDoc } = useUpdateUserDoc(Number(authUser?.id));
    const {
        data: userProfile,
        isLoading: isProfileLoading,
        isError,
        error,
    } = useGetUserProfileInfo(Number(userId));

    const watchCountryId = watch("country");
    const watchState = watch("state");

    const b2bUrl = process.env.NEXT_PUBLIC_B2B_URL;

    const { mutate: uploadPhoto } = useUpdateUserProfilePhoto(Number(userId));
    const { mutate: getGstData } = useGetGSTNumberVerificationMutation();
    // const { data: bankDetailsData } = useBankUserDetailList();
    const { data: countriesData } = useGetCountries();

    const { data: companyInfo, refetch: getCompanyDetails } =
        useGetCompanyDetailsId(id, {
            enabled: false,
        });

    const { data: cityData, refetch: refetchCities } = useGetCities(watchState);
    const { data: statesData, refetch: refetchStates } =
        useGetStates(watchCountryId);

    const profilePath = process.env.NEXT_PUBLIC_API_PIC_URL;

    const fileParts = userProfile?.user_profile_path?.split("/");
    const folderPath = fileParts?.slice(0, -1).join("/");
    const fileName =
        fileParts !== undefined ? fileParts[fileParts.length - 1] : "";

    const profilePicture = `${profilePath}${folderPath}/${encodeURIComponent(
        fileName
    )}`;

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setProfilePreview(reader.result as string);
            };
            reader.readAsDataURL(file);
            uploadPhoto({ file, folder: "profile" });
        }
    };

    const onSubmit = async (data: any) => {
        const payload = {
            id: id,
            user_email: authUser?.email,
            user_mobile: authUser?.mobile,
            branch: status || data?.branchDetails,
            company_name: data?.company_name,
            company_logo_path: logo,
            company_pancard_path: panProof,
            address_proof_path: addressProof,
            gst_proof_path: GSTProof,
            state: Number(data?.state),
            country: Number(data?.country),
            city: Number(data?.city) || 0,
            ...data,
            einvoice_enabled: data?.einvoice_enabled === "NOT ENABLED" || data?.einvoice_enabled === 0 || data?.einvoice_enabled === "0" ? 0 : 1,
        };
        console.log(payload);
        // companyProfileMutation.mutate({
        //   payload,
        // });

        if (id != 0 && companyInfo?.companyData) {
            companyDetailMutation.mutate(payload, {
                onSuccess: () => {
                    toast.success("Company Information update successfully!");
                    refetchCompanyDetails;
                },
                onError: (err: any) => {
                    toast.error(
                        err?.response?.data?.responseData?.response?.message ||
                        "Company Information updation failed"
                    );
                },
            });
        } else {
            console.log(payload);
            // return;
            companyProfileMutation.mutate(payload, {
                onSuccess: () => {
                    toast.success("Company Information updated successfully!");
                    refetchCompanyDetails();
                },
                onError: (err: any) => {
                    toast.error(
                        err?.response?.data?.responseData?.response?.message ||
                        "Company Information update failed"
                    );
                },
            });
        }
    };
    const onVerifyHandler = () => {
        const gstValue = getValues("gst")?.trim();

        if (!gstValue) {
            toast.error("Please enter a valid GST number.", {
                position: "top-right",
                autoClose: 5000,
            });
            // setIsGstVerified(false);
            return;
        }

        getGstData(gstValue, {
            onSuccess: (val: any) => {
                const isExisting = val?.data?.responseData?.response?.data?.isExisting;

                if (isExisting) {
                    toast.success("Company already exists!", {
                        position: "top-right",
                        autoClose: 5000,
                    });
                    // setIsGstVerified(false);
                } else {
                    toast.success("GST number verified successfully!", {
                        position: "top-right",
                        autoClose: 5000,
                    });
                    // setIsGstVerified(true);
                }
            },
            onError: (err: any) => {
                toast.error(
                    err?.response?.data?.responseData?.response?.message ||
                    "GST verification failed",
                    {
                        position: "top-right",
                        autoClose: 5000,
                    }
                );
                // setIsGstVerified(false);
            },
        });
    };

    const handleDocImageChange2 = (e: any, typeId: any) => {
        const file = e.target.files?.[0];
        if (file) {
            uploadDoc(
                {
                    file,
                    folder: String(userId),
                    doc_type_id: typeId,
                    user_id: String(userId) || "",
                },
                {
                    onSuccess: (response: any) => {
                        refetch();
                    },
                    onError: (err: any) => {
                        toast.error("Something went wrong. Please try again.");
                    },
                }
            );
        }
    };
    function getDocumentPath(docs: any[], typeId: number, basePath?: string) {
        const doc = docs?.find((d) => d.document_type_id === typeId);
        if (!doc || !doc.doc_file_upload) return "";

        const fileParts = doc.doc_file_upload.split("/");
        const folderPath = fileParts.slice(0, -1).join("/");
        const fileName = fileParts[fileParts.length - 1] || "";

        return `${basePath}${folderPath}/${encodeURIComponent(fileName)}`;
    }
    const panPicture = getDocumentPath(docData, 24, profilePath);
    const gstPicture = getDocumentPath(docData, 21, profilePath);
    const cPicture = getDocumentPath(docData, 22, profilePath);
    const addressPicture = getDocumentPath(docData, 2, profilePath);

    const handleAddressProofChange = (e: any, typeId: any) => {
        const file = e.target.files?.[0];
        // console.log(file);
        if (file) {
            uploadDoc(
                {
                    file,
                    folder: String(userId),
                    doc_type_id: typeId,
                    user_id: String(userId) || "",
                },
                {
                    onSuccess: (response: any) => {
                        // console.log("response: ", response?.uploadData?.doc_file_upload);
                        setAddressProof(response?.uploadData?.doc_file_upload);
                        refetch();
                    },
                    onError: (err: any) => {
                        toast.error("Something went wrong. Please try again.");
                    },
                }
            );
        }
    };

    const handleGSTProofChange = (e: any, typeId: any) => {
        const file = e.target.files?.[0];
        // console.log(file);
        if (file) {
            uploadDoc(
                {
                    file,
                    folder: String(userId),
                    doc_type_id: typeId,
                    user_id: String(userId) || "",
                },
                {
                    onSuccess: (response: any) => {
                        // console.log("response: ", response?.uploadData?.doc_file_upload);
                        setGSTProof(response?.uploadData?.doc_file_upload);
                        refetch();
                    },
                    onError: (err: any) => {
                        toast.error("Something went wrong. Please try again.");
                    },
                }
            );
        }
    };

    const handlePanProofChange = (e: any, typeId: any) => {
        const file = e.target.files?.[0];
        // console.log(file);
        if (file) {
            uploadDoc(
                {
                    file,
                    folder: String(userId),
                    doc_type_id: typeId,
                    user_id: String(userId) || "",
                },
                {
                    onSuccess: (response: any) => {
                        // console.log("response: ", response?.uploadData?.doc_file_upload);
                        setPanProof(response?.uploadData?.doc_file_upload);
                        refetch();
                    },
                    onError: (err: any) => {
                        toast.error("Something went wrong. Please try again.");
                    },
                }
            );
        }
    };

    const handleLogoChange = (e: any, typeId: any) => {
        const file = e.target.files?.[0];
        // console.log(file);
        if (file) {
            uploadDoc(
                {
                    file,
                    folder: String(userId),
                    doc_type_id: typeId,
                    user_id: String(userId) || "",
                },
                {
                    onSuccess: (response: any) => {
                        // console.log("response: ", response?.uploadData?.doc_file_upload);
                        setLogo(response?.uploadData?.doc_file_upload);
                        refetch();
                    },
                    onError: (err: any) => {
                        toast.error("Something went wrong. Please try again.");
                    },
                }
            );
        }
    };
    useEffect(() => {
        if (id && id > 0) {
            getCompanyDetails();
        }
    }, [id]);

    useEffect(() => {
        refetchStates(watchCountryId);
    }, [watchCountryId]);

    useEffect(() => {
        refetchCities(watchState);
    }, [watchState]);

    console.log(companyInfo);

    useEffect(() => {
        if (id != 0 && companyInfo) {
            console.log(companyInfo);
            setValue("company_name", companyInfo?.companyData?.company_name);
            setValue("company_size", companyInfo?.companyData?.company_size);
            setValue("company_address", companyInfo?.companyData?.company_address);
            setValue("country", String(companyInfo?.companyData?.country));
            setValue("state", String(companyInfo?.companyData?.state));
            setValue("city", String(companyInfo?.companyData?.city));
            setValue("pincode", companyInfo?.companyData?.pincode);
            setValue("email", companyInfo?.companyData?.email);
            setValue(
                "contact_person_name",
                companyInfo?.companyData?.contact_person_name
            );
            setValue("gst", companyInfo?.companyData?.service_tax_gst);
            setValue("pancard_no", companyInfo?.companyData?.pancard_no);
            setValue("mobile_no", companyInfo?.companyData?.mobile_no);
            setValue("landline_no", companyInfo?.companyData?.landline_no);
            setValue("website_url", companyInfo?.companyData?.website_url);
            setValue("center_code", companyInfo?.gstData?.center_code);
            setValue(
                "einvoice_enabled",
                companyInfo?.gstData?.einvoice_enabled == 0 ? "NOT ENABLED" : "ENABLED"
            );
            setValue("constitution", companyInfo?.gstData?.constitution);
            setValue("gst_expiry_date", companyInfo?.gstData?.expiry_date);
            setValue("filings", companyInfo?.gstData?.filings);
            setValue("gst_type", companyInfo?.gstData?.gst_type);
            setValue("last_updated_on", companyInfo?.gstData?.last_updated_on);
            setValue("legal_name", companyInfo?.gstData?.legal_name);
            setValue("nature_of_business", companyInfo?.gstData?.nature_of_business);
            setValue("primary_address", companyInfo?.gstData?.primary_address);
            setValue("state_code", companyInfo?.gstData?.state_code);
            setValue("gst_state", companyInfo?.gstData?.state);
            setValue("trade_name", companyInfo?.gstData?.trade_name);
            setValue("registered_on", companyInfo?.gstData?.registered_on);
            setStatus(companyInfo?.companyInfo?.branch ?? "1");
            setValue("pan", companyInfo?.gstData?.pan);
            setAddressProof(`${companyInfo?.companyData?.address_proof_path}`);
            setGSTProof(`${companyInfo?.companyData?.gst_proof_path}`);
            setPanProof(`${companyInfo?.companyData?.company_pancard_path}`);
            setLogo(`${companyInfo?.companyData?.company_logo_path}`);
        }
    }, [id, companyInfo]);

    return (
        <form
            action=""
            onSubmit={handleSubmit(onSubmit)}
            className="w-full bg-white"
        >
            <div className="flex justify-between">
                <div className="bg-white/70 backdrop-blur-sm border border-white/20 overflow-hidden w-[70%]">
                    <div className="p-8 space-y-8">
                        {/* Basic Information */}
                        <div className="space-y-3">
                            <div className="grid md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label className="block text-[10px] font-medium text-gray-700">
                                        Company Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        {...register("company_name")}
                                        className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[10px] sm:text-[10px]"
                                        placeholder="Enter Company Name"
                                    />
                                    {errors?.company_name && (
                                        <p className="text-[10px] text-red-500">
                                            {errors.company_name.message}
                                        </p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <label className="block text-[10px] font-medium text-gray-700">
                                        Company Size <span className="text-red-600">*</span>
                                    </label>
                                    <select
                                        {...register(`company_size`)}
                                        className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[10px] sm:text-[10px]"
                                    >
                                        <option value="">Select Company Size</option>
                                        {companySizes.map((item: any) => (
                                            <option key={item.value} value={item.value}>
                                                {item.label}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.company_size && (
                                        <p className="text-xs text-red-600 mt-1">
                                            {errors.company_size.message}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="grid md:grid-cols-2 gap-3">
                                <div className="space-y-2">
                                    <label className="block text-[10px] font-medium text-gray-700">
                                        Company Address <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        {...register("company_address")}
                                        className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[10px] sm:text-[10px]"
                                        placeholder="Enter Company Address"
                                    />
                                    {errors?.company_address && (
                                        <p className="text-[10px] text-red-500">
                                            {errors.company_address.message}
                                        </p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <label className="block text-[10px] font-medium text-gray-700">
                                        Country <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        {...register("country")}
                                        name="country"
                                        id=""
                                        className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[10px] sm:text-[10px] max-h-[60px]"
                                    >
                                        <option value="">Select Country</option>
                                        {countriesData?.map((country: any) => (
                                            <option key={country.id} value={country.id}>
                                                {country.name}
                                            </option>
                                        ))}
                                    </select>
                                    {errors?.country && (
                                        <p className="text-[10px] text-red-500">
                                            {errors.country.message}
                                        </p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <label className="block text-[10px] font-medium text-gray-700">
                                        State <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        {...register("state")}
                                        // name="state"
                                        // id=""
                                        className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[10px] sm:text-[10px] max-h-[60px]"
                                    >
                                        <option value="">Select State</option>
                                        {statesData?.map((state: any) => (
                                            <option key={state.id} value={state.id}>
                                                {state.name}
                                            </option>
                                        ))}
                                    </select>
                                    {/* {errors?.state && (
                    <p className="text-[10px] text-red-500">
                      {errors.state.message}
                    </p>
                  )} */}
                                </div>

                                {/* <div className="space-y-2">
                  <label className="block text-[10px] font-medium text-gray-700">
                    State <span className="text-red-500">*</span>
                  </label>
                  <select
                    {...register("state")}
                    name="country"
                    id=""
                    className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[10px] sm:text-[10px] max-h-[60px]"
                  >
                    <option value="">Select State</option>
                    {statesData?.map((state: any) => (
                      <option value={state.id}>{state.name}</option>
                    ))}
                  </select>
                  {errors?.state && (
                    <p className="text-[10px] text-red-500">
                      {errors.state.message}
                    </p>
                  )}
                </div> */}

                                <div className="space-y-2">
                                    <label className="block text-[10px] font-medium text-gray-700">
                                        City <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        {...register("city")}
                                        className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[10px] sm:text-[10px] max-h-[60px]"
                                    >
                                        <option value="">Select City</option>
                                        {cityData?.map((city: any) => (
                                            <option key={city.id} value={city.id}>
                                                {city.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="grid md:grid-cols-2 gap-3">
                                <div className="space-y-2">
                                    <label className="block text-[10px] font-medium text-gray-700">
                                        Pincode
                                    </label>
                                    <input
                                        {...register("pincode")}
                                        className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[10px] sm:text-[10px]"
                                        placeholder="Enter Pincode"
                                    />
                                </div>

                                <div className="space-y-2 relative">
                                    <label className="block text-[10px] font-medium text-gray-700">
                                        Service Tax/GST <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        {...register("gst")}
                                        className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[10px]"
                                        placeholder="Enter Service Tax/GST"
                                        disabled={id == 0 && companyInfo?.gstData?.valid}
                                    />
                                    {id == 0 && companyInfo?.gstData?.valid == 1 ? (
                                        <div className="absolute top-1/2 right-[15px]">
                                            <CheckCircle className="text-green-500 w-4 h-4" />
                                        </div>
                                    ) : (
                                        <button
                                            onClick={onVerifyHandler}
                                            // disabled={getCompanyDetailData}
                                            type="button"
                                            className="text-[10px]  py-1 px-3 cursor-pointer border border-transparent rounded-md shadow-md  font-medium float-right text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 transform hover:scale-105"
                                        >
                                            Verify
                                        </button>
                                    )}
                                    {errors?.gst && (
                                        <p className="text-[10px] text-red-500">
                                            {errors.gst.message}
                                        </p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <label className="block text-[10px] font-medium text-gray-700">
                                        Company PAN No. <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        {...register("pancard_no")}
                                        className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[10px] sm:text-[10px]"
                                        placeholder="Enter PAN No."
                                        // disabled={getCompanyDetailData}
                                    />
                                    {errors?.pancard_no && (
                                        <p className="text-[10px] text-red-500">
                                            {errors.pancard_no.message}
                                        </p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <label className="block text-[10px] font-medium text-gray-700">
                                        Contact Person
                                    </label>
                                    <input
                                        {...register("contact_person_name")}
                                        className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[10px] sm:text-[10px]"
                                        placeholder="Enter Contact Person"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <label className="block text-[10px] font-medium text-gray-700">
                                        Phone No.
                                    </label>
                                    <input
                                        {...register("landline_no")}
                                        minLength={10}
                                        className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[10px] sm:text-[10px]"
                                        placeholder="Enter Phone No."
                                    />
                                </div>

                                <div className="space-y-2">
                                    <label className="block text-[10px] font-medium text-gray-700">
                                        Mobile No.
                                    </label>
                                    <input
                                        {...register("mobile_no")}
                                        minLength={10}
                                        className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[10px] sm:text-[10px]"
                                        placeholder="Enter Mobile No."
                                    />
                                </div>

                                <div className="space-y-2">
                                    <label className="block text-[10px] font-medium text-gray-700">
                                        Email
                                    </label>
                                    <input
                                        type="email"
                                        {...register("email")}
                                        className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[10px] sm:text-[10px]"
                                        placeholder="Enter Email"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <label className="block text-[10px] font-medium text-gray-700">
                                        website_url
                                    </label>
                                    <input
                                        {...register("website_url")}
                                        className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[10px] sm:text-[10px]"
                                        placeholder="Enter website_url"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="block text-[10px] font-medium text-gray-700">
                                        GST Number
                                        {/* <span className="text-red-500">*</span> */}
                                    </label>
                                    <input
                                        {...register("gst_number")}
                                        className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[10px] sm:text-[10px]"
                                        placeholder="Enter GST Number"
                                    />
                                    {
                                        errors.gst_number && (<p className="text-red-500">
                                            {errors.gst_number.message}
                                        </p>)
                                    }
                                </div>

                                <div className="space-y-2">
                                    <label className="block text-[10px] font-medium text-gray-700">
                                        GST Status
                                        {/* <span className="text-red-500">*</span> */}
                                    </label>
                                    <input
                                        {...register("active")}
                                        className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[10px] sm:text-[10px]"
                                        placeholder="Enter GST Status"
                                    />
                                    {errors?.active && (
                                        <p className="text-[10px] text-red-500">
                                            {errors.active.message}
                                        </p>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <label className="block text-[10px] font-medium text-gray-700">
                                        GST Centre Code <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        {...register("center_code")}
                                        className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[10px] sm:text-[10px]"
                                        placeholder="Enter GST Centre Code"
                                    />
                                    {errors?.center_code && (
                                        <p className="text-[10px] text-red-500">
                                            {errors.center_code.message}
                                        </p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <label className="block text-[10px] font-medium text-gray-700">
                                        GST Constituttion <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        {...register("constitution")}
                                        className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[10px] sm:text-[10px]"
                                        placeholder="Enter GST Constitution"
                                    />
                                    {errors?.constitution && (
                                        <p className="text-[10px] text-red-500">
                                            {errors.constitution.message}
                                        </p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <label className="block text-[10px] font-medium text-gray-700">
                                        GST E-Incoice Enabled{" "}
                                        <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        {...register("einvoice_enabled")}
                                        className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[10px] sm:text-[10px]"
                                        placeholder="Enter GST E-Invoice Enabled"
                                    />

                                    {errors?.einvoice_enabled && (
                                        <p className="text-[10px] text-red-500">
                                            {errors.einvoice_enabled.message}
                                        </p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <label className="block text-[10px] font-medium text-gray-700">
                                        GST Expiry Date <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        {...register("gst_expiry_date")}
                                        className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[10px] sm:text-[10px]"
                                        placeholder="Enter GST Expiry Date"
                                    />
                                    {errors?.gst_expiry_date && (
                                        <p className="text-[10px] text-red-500">
                                            {errors.gst_expiry_date.message}
                                        </p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <label className="block text-[10px] font-medium text-gray-700">
                                        GST Filings <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        {...register("filings")}
                                        className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[10px] sm:text-[10px]"
                                        placeholder="Enter GST Filings"
                                    />
                                    {errors?.filings && (
                                        <p className="text-[10px] text-red-500">
                                            {errors.filings.message}
                                        </p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <label className="block text-[10px] font-medium text-gray-700">
                                        GST Type <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        {...register("gst_type")}
                                        className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[10px] sm:text-[10px]"
                                        placeholder="Enter GST Type"
                                    />
                                    {errors?.company_name && (
                                        <p className="text-[10px] text-red-500">
                                            {errors.company_name.message}
                                        </p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <label className="block text-[10px] font-medium text-gray-700">
                                        GST Last Updated On <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        {...register("last_updated_on")}
                                        type="date"
                                        className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[10px] sm:text-[10px]"
                                        placeholder="Enter Last Updated On"
                                    />
                                    {errors?.last_updated_on && (
                                        <p className="text-[10px] text-red-500">
                                            {errors.last_updated_on.message}
                                        </p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <label className="block text-[10px] font-medium text-gray-700">
                                        GST Legal Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        {...register("legal_name")}
                                        className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[10px] sm:text-[10px]"
                                        placeholder="Enter Legal Name"
                                    />
                                    {errors?.legal_name && (
                                        <p className="text-[10px] text-red-500">
                                            {errors.legal_name.message}
                                        </p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <label className="block text-[10px] font-medium text-gray-700">
                                        Nature of Business <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        {...register("nature_of_business")}
                                        className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[10px] sm:text-[10px]"
                                        placeholder="Enter Nature of Business"
                                    />
                                    {errors?.nature_of_business && (
                                        <p className="text-[10px] text-red-500">
                                            {errors.nature_of_business.message}
                                        </p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <label className="block text-[10px] font-medium text-gray-700">
                                        GST Primary Address <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        {...register("primary_address")}
                                        className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[10px] sm:text-[10px]"
                                        placeholder="Enter Primary Address"
                                    />
                                    {errors?.primary_address && (
                                        <p className="text-[10px] text-red-500">
                                            {errors.primary_address.message}
                                        </p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <label className="block text-[10px] font-medium text-gray-700">
                                        GST Registered On <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        {...register("registered_on")}
                                        className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[10px] sm:text-[10px]"
                                        placeholder="Enter Registered On"
                                    />
                                    {errors?.registered_on && (
                                        <p className="text-[10px] text-red-500">
                                            {errors.registered_on.message}
                                        </p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <label className="block text-[10px] font-medium text-gray-700">
                                        Gst State <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        {...register("gst_state")}
                                        className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[10px] sm:text-[10px]"
                                        placeholder="Enter State"
                                    />
                                    {errors?.gst_state && (
                                        <p className="text-[10px] text-red-500">
                                            {errors.gst_state.message}
                                        </p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <label className="block text-[10px] font-medium text-gray-700">
                                        GST State Code <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        {...register("state_code")}
                                        className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[10px] sm:text-[10px]"
                                        placeholder="Enter Code"
                                    />
                                    {errors?.state_code && (
                                        <p className="text-[10px] text-red-500">
                                            {errors.state_code.message}
                                        </p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <label className="block text-[10px] font-medium text-gray-700">
                                        Trade Name<span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        {...register("trade_name")}
                                        className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[10px] sm:text-[10px]"
                                        placeholder="Enter Trade Name"
                                    />
                                    {errors?.trade_name && (
                                        <p className="text-[10px] text-red-500">
                                            {errors.trade_name.message}
                                        </p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <label className="block text-[10px] font-medium text-gray-700">
                                        GST Pan<span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        {...register("pan")}
                                        className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[10px] sm:text-[10px]"
                                        placeholder="Enter Pan"
                                    />
                                    {errors?.pan && (
                                        <p className="text-[10px] text-red-500">
                                            {errors.pan.message}
                                        </p>
                                    )}
                                </div>

                                <div className="flex flex-col py-1">
                                    <div className="grid grid-cols-2 gap-8 py-2 sm:gap-4 sm:grid-cols-3 md:flex md:items-center">
                                        {[
                                            { id: "0", label: "Head Office" },
                                            { id: "1", label: "Branch" },
                                        ].map((option) => (
                                            <div
                                                key={option.id}
                                                className="flex gap-1 items-center text-sm sm:gap-2"
                                            >
                                                <div
                                                    onClick={() => handleStatusToggle(option.id)}
                                                    className={`rounded-full border-2 border-gray-500 cursor-pointer bg-white flex items-center px-0.5 transition-all duration-300
                    ${status === option.id ? "justify-end" : "justify-start"}
                    min-w-9.5 min-h-4.5 sm:min-w-13 sm:min-h-6.5`}
                                                >
                                                    <div className="h-3.5 w-3.5 sm:h-4 sm:w-4 rounded-full bg-gray-500 shadow-md transition-all duration-300" />
                                                </div>
                                                <p className="text-[10px] font-medium text-gray-700">
                                                    {option.label}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="img-upload w-[30%] py-12 pr-6">
                    <div className="flex flex-col items-end gap-6">
                        {/* <div className="flex flex-col items-center space-y-3">
              <div className="relative group">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    onChange={(e) => {
                      handleAddressProofChange(e, "2");
                    }}
                    className="hidden"
                    accept="image/*"
                  />
                  <div className="relative">
                    {addressProof ? (
                      <img
                        src={`${b2bUrl}${addressProof}`}
                        className="w-35 h-30 object-cover border-4 border-white shadow-md transition-all duration-300"
                        alt="Address Proof"
                      />
                    ) : (
                      <div className="w-32 h-32 flex items-center justify-center border-3 border-dashed border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100 group-hover:border-blue-400 group-hover:from-blue-50 group-hover:to-blue-100 transition-all duration-300">
                        <UploadCloud className="w-10 h-10 text-gray-400 group-hover:text-blue-500 transition-colors" />
                      </div>
                    )}
                    <div className="absolute -bottom-1 -right-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white p-2.5 rounded-full shadow-md hover:shadow-md transition-shadow">
                      <Camera className="w-4 h-4" />
                    </div>
                  </div>
                </label>
              </div>
              <div className="text-center">
                <p className="text-[10px] font-medium text-gray-700">
                  Address Proof
                </p>
                <p className="text-xs text-gray-500">
                  Click to upload or change
                </p>
              </div>
            </div> */}

                        <ImageEditor
                            image={addressProof}
                            setImage={setAddressProof}
                            docTypeId={2}
                            label="Address Proof"
                        />

                        {/* Registration Certificate */}
                        {/* <div className="flex flex-col items-center space-y-3">
              <div className="relative group">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    onChange={(e) => {
                      handleGSTProofChange(e, "21");
                    }}
                    className="hidden"
                    accept="image/*"
                  />
                  <div className="relative">
                    {GSTProof ? (
                      <img
                        src={`${b2bUrl}${GSTProof}`}
                        className="w-35 h-30 object-cover border-4 border-white shadow-md transition-all duration-300"
                        alt="GST Proof"
                      />
                    ) : (
                      <div className="w-32 h-32 flex items-center justify-center border-3 border-dashed border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100 group-hover:border-blue-400 group-hover:from-blue-50 group-hover:to-blue-100 transition-all duration-300">
                        <UploadCloud className="w-10 h-10 text-gray-400 group-hover:text-blue-500 transition-colors" />
                      </div>
                    )}
                    <div className="absolute -bottom-1 -right-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white p-2.5 rounded-full shadow-md hover:shadow-md transition-shadow">
                      <Camera className="w-4 h-4" />
                    </div>
                  </div>
                </label>
              </div>
              <div className="text-center">
                <p className="text-[10px] font-medium text-gray-700">
                  GST Proof
                </p>
                <p className="text-xs text-gray-500">
                  Click to upload or change
                </p>
              </div>
            </div> */}

                        <ImageEditor
                            image={GSTProof}
                            setImage={setGSTProof}
                            docTypeId={21}
                            label="GST Proof"
                        />

                        {/* Company Pan Card */}
                        {/* <div className="flex flex-col items-center space-y-3">
              <div className="relative group">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    onChange={(e) => {
                      handlePanProofChange(e, "24");
                    }}
                    className="hidden"
                    accept="image/*"
                  />
                  <div className="relative">
                    {panProof ? (
                      <img
                        src={`${b2bUrl}${panProof}`}
                        className="w-35 h-30 object-cover border-4 border-white shadow-md transition-all duration-300"
                        alt="PAN Proof"
                      />
                    ) : (
                      <div className="w-32 h-32 flex items-center justify-center border-3 border-dashed border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100 group-hover:border-blue-400 group-hover:from-blue-50 group-hover:to-blue-100 transition-all duration-300">
                        <UploadCloud className="w-10 h-10 text-gray-400 group-hover:text-blue-500 transition-colors" />
                      </div>
                    )}
                    <div className="absolute -bottom-1 -right-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white p-2.5 rounded-full shadow-md hover:shadow-md transition-shadow">
                      <Camera className="w-4 h-4" />
                    </div>
                  </div>
                </label>
              </div>
              <div className="text-center">
                <p className="text-[10px] font-medium text-gray-700">
                  PAN Proof
                </p>
                <p className="text-xs text-gray-500">
                  Click to upload or change
                </p>
              </div>
            </div> */}

                        <ImageEditor
                            image={panProof}
                            setImage={setPanProof}
                            docTypeId={24}
                            label="Company PAN Proof"
                        />

                        {/* Company Logo */}
                        {/* <div className="flex flex-col items-center space-y-3">
              <div className="relative group">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    onChange={(e) => {
                      handleLogoChange(e, "22");
                    }}
                    className="hidden"
                    accept="image/*"
                  />
                  <div className="relative">
                    {logo ? (
                      <img
                        src={`${b2bUrl}${logo}`}
                        className="w-35 h-30 object-cover border-4 border-white shadow-md transition-all duration-300"
                        alt="Logo"
                      />
                    ) : (
                      <div className="w-32 h-32 flex items-center justify-center border-3 border-dashed border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100 group-hover:border-blue-400 group-hover:from-blue-50 group-hover:to-blue-100 transition-all duration-300">
                        <UploadCloud className="w-10 h-10 text-gray-400 group-hover:text-blue-500 transition-colors" />
                      </div>
                    )}
                    <div className="absolute -bottom-1 -right-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white p-2.5 rounded-full shadow-md hover:shadow-md transition-shadow">
                      <Camera className="w-4 h-4" />
                    </div>
                  </div>
                </label>
              </div>
              <div className="text-center">
                <p className="text-[10px] font-medium text-gray-700">Logo</p>
                <p className="text-xs text-gray-500">
                  Click to upload or change
                </p>
              </div>
            </div> */}

                        <ImageEditor
                            image={logo}
                            setImage={setLogo}
                            docTypeId={22}
                            label="Logo"
                        />
                    </div>
                </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end gap-4 p-6">
                <button
                    type="button"
                    className="text-[10px]  py-1 px-3 border border-gray-300 rounded-md shadow-sm  font-medium text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 cursor-pointer"
                >
                    Reset
                </button>
                <button
                    type="submit"
                    className="text-[10px]  py-1 px-3 cursor-pointer border border-transparent rounded-md shadow-md  font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 transform hover:scale-105"
                >
                    {id == 0 && companyInfo?.companyData ? "Update" : "Save"}
                </button>
            </div>
        </form>
    );
}
